import type { Theme } from '@mui/material';
import { Fade, Slide, Snackbar, styled } from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import type { ReactElement } from 'react';

import { useDetection } from '@utility/checkout-utils';

import { ChkIconToastClose } from '../icons';

import { CHK_TOAST_VARIANT } from './ChkToast.constants';
import type {
  ChkToastHandlers,
  ChkToastProps,
  ChkToastVariant,
  StyledIconWrapperProps,
} from './ChkToast.types';

const SlideAndFadeTransition = (
  props: TransitionProps & { children: ReactElement }
) => {
  const { isMobile } = useDetection();
  const direction = isMobile ? 'down' : 'up';

  return (
    <Slide
      {...props}
      direction={direction}
      timeout={600}
      easing="cubic-bezier(0.2, 0, 0.2, 1)"
    >
      {/* div needed here to make Slide work with Fade */}
      <div>
        <Fade {...props} timeout={600} />
      </div>
    </Slide>
  );
};

export const ChkToast = forwardRef<ChkToastHandlers, ChkToastProps>(
  (
    {
      autoHideDuration,
      onClose,
      children,
      alignCloseIcon,
      variant,
      closeButtonProps,
      ...rest
    }: ChkToastProps,
    ref
  ) => {
    const { isMobile } = useDetection();
    const anchorOrigin = isMobile
      ? ({ vertical: 'top', horizontal: 'center' } as const)
      : ({ vertical: 'bottom', horizontal: 'center' } as const);
    const toastRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(
      ref,
      () => ({
        focusToast() {
          toastRef.current?.focus();
        },
      }),
      []
    );

    return (
      <StyledSnackbar
        anchorOrigin={anchorOrigin}
        sx={{ width: '100%', outline: 'none' }}
        autoHideDuration={autoHideDuration || 3000}
        onClose={onClose}
        slots={{
          transition: SlideAndFadeTransition,
        }}
        {...rest}
      >
        <StyledSnackbarContent variant={variant}>
          <div role="alert" tabIndex={-1} ref={toastRef}>
            {children}
          </div>
          <StyledIconButton
            onClick={onClose}
            alignSelf={alignCloseIcon}
            {...closeButtonProps}
          >
            <ChkIconToastClose />
          </StyledIconButton>
        </StyledSnackbarContent>
      </StyledSnackbar>
    );
  }
);

const StyledSnackbar = styled(Snackbar)(({ theme }) => ({
  left: 0,
  right: 0,
  margin: '0 auto',
  padding: `0 ${theme.spacing(6)}`,

  '& > div': {
    width: '100%',
  },
}));

const StyledSnackbarContent = styled('div')<Pick<ChkToastProps, 'variant'>>(
  ({ theme, variant }) => ({
    ...theme.typography.body2,
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: getToastBackgroundColor(theme, variant),
    color: theme.tokens.toastsText,
    borderRadius: theme.spacing(3),
    padding: theme.spacing(5, 6),
    gap: theme.spacing(5),
    width: '100%',
    margin: '0 auto',

    [theme.breakpoints.up('xs')]: {
      width: '41.6rem',
    },
  })
);

const StyledIconButton = styled('button')<StyledIconWrapperProps>(
  ({ alignSelf, theme }) => ({
    cursor: 'pointer',
    display: 'flex',
    flex: '0 0 24px',
    height: '24px',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: alignSelf || 'center',
    color: theme.tokens.toastsText,
    background: 'none',
    border: 'none',
    padding: 0,
  })
);

function getToastBackgroundColor(theme: Theme, variant?: ChkToastVariant) {
  if (variant === CHK_TOAST_VARIANT.ERROR) {
    return theme.tokens.toastsErrorBackground;
  }

  return theme.tokens.toastsInfoBackground;
}
